import { ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import SiteAdminService, { isCustomDomainsUnconfigured, isCustomDomainsNotInPlan, isCustomDomainsLimit } from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { isApexDomain } from '@/Utils/siteDomains'

/** Pending domains are re-checked against Cloudflare this often while the page is open. */
export const DOMAIN_POLL_INTERVAL_MS = 30_000

/**
 * One site's custom domains (castr `/sites/:id/domains*`, control-plane API §6d).
 * Every write returns the refreshed public site, which is pushed into the
 * `Sites` store so the header / overview see `customDomains` and
 * `canonicalHost` without another fetch. While any domain is pending the list
 * is re-read with `refresh=1` every 30 s; the timer stops on unmount.
 */
export function useSiteDomains(siteId) {
	const store = useStore()
	const domains = ref([])
	// provider / ingressIps arrive with castr builds that route the sites through
	// DigitalOcean App Platform (apex domains get A records); older builds omit them.
	// `allowed: false` once castr says the plan lacks `sites:custom-domain` (older builds omit it).
	const meta = ref({ configured: true, allowed: null, provider: null, cnameTarget: '', ingressIps: [], max: 3, used: 0, primaryHost: '', canonicalHost: null })
	const loading = ref(false)
	const refreshing = ref(false)
	const loaded = ref(false)
	const error = ref(null)
	const unconfigured = ref(false)
	// hostname → 'verify' | 'remove' | 'primary' while a request is in flight
	const busy = ref({})
	let timer = null

	const id = () => (typeof siteId === 'function' ? siteId() : siteId.value ?? siteId)

	const hasPending = computed(() => domains.value.some((d) => d.status === 'pending'))
	const allowed = computed(() => meta.value.allowed !== false)
	const canAdd = computed(() => meta.value.configured && !unconfigured.value && domains.value.length < meta.value.max)
	// the plan's per-site cap (`sites:max-custom-domains`) is used up
	const atLimit = computed(() => meta.value.configured && !unconfigured.value && allowed.value && domains.value.length >= meta.value.max)
	const remaining = computed(() => Math.max(0, meta.value.max - domains.value.length))

	function applyList(res) {
		if (!res) return
		domains.value = res.domains || []
		meta.value = {
			configured: res.configured !== false,
			allowed: res.allowed ?? true,
			provider: res.provider || null,
			cnameTarget: res.cnameTarget || meta.value.cnameTarget || '',
			ingressIps: Array.isArray(res.ingressIps) ? res.ingressIps : [],
			max: res.max ?? meta.value.max,
			used: res.used ?? domains.value.length,
			primaryHost: res.primaryHost || meta.value.primaryHost || '',
			canonicalHost: res.canonicalHost ?? null,
		}
		unconfigured.value = res.configured === false
		schedule()
	}

	// castr returns toPublic(site) after each write: its customDomains already
	// carry phase/records/isPrimary, so the list is rebuilt from it.
	function applySite(site) {
		if (!site || !site.id) return
		store.commit('Sites/UPSERT_SITE', site)
		if (Array.isArray(site.customDomains)) domains.value = site.customDomains
		meta.value = { ...meta.value, used: domains.value.length, primaryHost: site.primaryHost || meta.value.primaryHost, canonicalHost: site.canonicalHost ?? null }
		schedule()
	}

	/** A 403 `custom_domains_not_in_plan` on any write flips the page to its locked state. */
	function markNotInPlan(err) {
		if (!isCustomDomainsNotInPlan(err)) return false
		meta.value = { ...meta.value, allowed: false }
		stop()
		return true
	}

	/** A 403 `custom_domains_limit` on add (the list was stale): adopt castr's cap. */
	function markLimit(err) {
		if (!isCustomDomainsLimit(err)) return false
		const max = Number(err.payload?.max)
		meta.value = { ...meta.value, max: Number.isFinite(max) ? max : domains.value.length }
		return true
	}

	function fail(err) {
		if (isCustomDomainsUnconfigured(err)) {
			unconfigured.value = true
			error.value = null
			domains.value = []
			return
		}
		error.value = err
	}

	const site = () => store.getters['Sites/siteById'](id()) || id()

	async function load({ refresh = false } = {}) {
		const flag = refresh ? refreshing : loading
		flag.value = true
		const pendingBefore = refresh ? domains.value.filter((d) => d.status === 'pending').map((d) => d.hostname) : []
		try {
			applyList(await SiteAdminService.listDomains(id(), { refresh }))
			for (const d of domains.value) {
				if (d.status === 'active' && pendingBefore.includes(d.hostname)) {
					trackSites(SITES_EVENTS.CUSTOM_DOMAIN_VERIFIED, site(), { domain: d.hostname, method: 'poll' })
				}
			}
			error.value = null
			loaded.value = true
		} catch (err) {
			fail(err)
			// keep polling on a transient error so a flaky check does not freeze the page
			if (refresh && !isCustomDomainsUnconfigured(err)) schedule()
		} finally {
			flag.value = false
		}
		return domains.value
	}

	async function withBusy(hostname, kind, fn) {
		busy.value = { ...busy.value, [hostname]: kind }
		try {
			return await fn()
		} finally {
			const next = { ...busy.value }
			delete next[hostname]
			busy.value = next
		}
	}

	/** @return {Promise<object>} the new pending domain */
	async function add(hostname) {
		const res = await SiteAdminService.addDomain(id(), hostname)
		applySite(res?.site)
		if (res?.domain && !domains.value.some((d) => d.hostname === res.domain.hostname)) domains.value = [...domains.value, res.domain]
		trackSites(SITES_EVENTS.CUSTOM_DOMAIN_ADDED, site(), { domain: hostname, apex: isApexDomain(hostname) })
		return res?.domain
	}

	/** Re-read hostname + certificate status from Cloudflare. @return {Promise<{ domain, activated }>} */
	function verify(hostname) {
		return withBusy(hostname, 'verify', async () => {
			const res = await SiteAdminService.verifyDomain(id(), hostname)
			applySite(res?.site)
			if (res?.activated) trackSites(SITES_EVENTS.CUSTOM_DOMAIN_VERIFIED, site(), { domain: hostname, method: 'manual' })
			return res
		})
	}

	function remove(hostname) {
		return withBusy(hostname, 'remove', async () => {
			const res = await SiteAdminService.removeDomain(id(), hostname)
			applySite(res?.site)
			domains.value = domains.value.filter((d) => d.hostname !== hostname)
			meta.value = { ...meta.value, used: domains.value.length }
			trackSites(SITES_EVENTS.CUSTOM_DOMAIN_REMOVED, site(), { domain: hostname })
			return res
		})
	}

	/** Pass `meta.primaryHost` (the Castr address) to make it canonical again. */
	function setPrimary(hostname) {
		return withBusy(hostname, 'primary', async () => {
			const res = await SiteAdminService.setPrimaryDomain(id(), hostname)
			applySite(res?.site)
			if (res?.canonicalHost) meta.value = { ...meta.value, canonicalHost: res.canonicalHost }
			if (hostname !== (meta.value.primaryHost || res?.site?.primaryHost)) {
				trackSites(SITES_EVENTS.CUSTOM_DOMAIN_SET_PRIMARY, site(), { domain: hostname })
			}
			return res
		})
	}

	function schedule() {
		stop()
		if (!hasPending.value || unconfigured.value || !allowed.value) return
		timer = setTimeout(() => { timer = null; load({ refresh: true }) }, DOMAIN_POLL_INTERVAL_MS)
	}

	function stop() {
		if (timer) clearTimeout(timer)
		timer = null
	}

	onBeforeUnmount(stop)

	return {
		domains, meta, loading, refreshing, loaded, error, unconfigured, busy,
		hasPending, allowed, canAdd, atLimit, remaining,
		load, add, verify, remove, setPrimary, stop, markNotInPlan, markLimit,
	}
}
